import {
  faArrowUpRightFromSquare,
  faEdit,
  faTrashCan
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import type React from 'react';
import { useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { LargeCard } from '@/components/LargeCard';
import PERMISSIONS from '@/constants/auth.constants';
import useConfirm from '@/hooks/useConfirm';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import { useCanaryTokens } from './AdminCanaryTokens.hooks';
import type {
  CanaryTokenDTO,
  CanaryTokenFormFields
} from './AdminCanaryTokens.types';
import AdminCanaryTokensDialog from './AdminCanaryTokensDialog';

const AdminCanaryTokens: React.FC = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingToken, setEditingToken] = useState<CanaryTokenDTO | null>(null);

  const { ConfirmationModal: DeleteModal, confirm: confirmDelete } = useConfirm(
    {
      title: 'Delete Canarytoken',
      message:
        'Are you sure you want to delete this Canarytoken? This action cannot be undone.',
      button: 'Delete'
    }
  );

  const {
    data: canaryTokens,
    isLoading,
    updateToken,
    deleteToken
  } = useCanaryTokens(selectedOrganization?.label || '');

  const handleOpenDialog = (token: CanaryTokenDTO) => {
    setEditingToken(token);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingToken(null);
  };

  const handleFormSubmit = (data: CanaryTokenFormFields) => {
    if (!selectedOrganization || !editingToken) return;

    updateToken.mutate({
      id: editingToken.id,
      data: {
        description: data.description,
        active: data.active
      }
    });
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirmDelete();

    if (confirmed) {
      deleteToken.mutate(id);
    }
  };

  return (
    <Box>
      <LargeCard title="Canarytokens">
        <Typography sx={{ mb: 2 }} variant="body1">
          In this section, you can monitor all active Canarytokens. To connect
          tokens with Pondurance Scope and activate monitoring you must
          configure the{' '}
          <a href="https://canarytokens.org/" target="_blank" rel="noreferrer">
            Canarytoken
          </a>{' '}
          with the following webhook URL: <br />
          <Typography
            sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
            component="span"
          >
            {selectedOrganization?.webhook_url_ct}
          </Typography>
          <CopyToClipboard text={selectedOrganization?.webhook_url_ct} />
        </Typography>

        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : (
          <>
            <Typography sx={{ mb: 1 }} variant="h6">
              Active Canary Tokens
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Token</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center">Triggered</TableCell>
                    <TableCell>First Triggered</TableCell>
                    <TableCell>Last Triggered</TableCell>
                    <TableCell align="center">Enabled</TableCell>
                    <CheckPermissions permissions={[PERMISSIONS.ADMIN_UPDATE]}>
                      <TableCell>Actions</TableCell>
                    </CheckPermissions>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {canaryTokens?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">
                          No Canarytokens configured
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    canaryTokens?.map((token) => (
                      <TableRow key={token.id}>
                        <TableCell sx={{ width: '13rem' }}>
                          {token.token}
                        </TableCell>
                        <TableCell>{token.description || '-'}</TableCell>
                        <TableCell sx={{ width: '9rem' }} align="center">
                          {token.count || 0}
                        </TableCell>
                        <TableCell sx={{ width: '12rem' }}>
                          {token.created_at
                            ? dayjs
                                .utc(token.created_at)
                                .toDate()
                                .toLocaleString()
                            : '-'}
                        </TableCell>
                        <TableCell sx={{ width: '12rem' }}>
                          {token.updated_at
                            ? dayjs
                                .utc(token.updated_at)
                                .toDate()
                                .toLocaleString()
                            : '-'}
                        </TableCell>
                        <TableCell align="center" sx={{ width: '6rem' }}>
                          {token.active ? 'Yes' : 'No'}
                        </TableCell>
                        <CheckPermissions
                          permissions={[PERMISSIONS.ADMIN_UPDATE]}
                        >
                          <TableCell align="right" sx={{ width: '8rem' }}>
                            <Stack
                              direction="row"
                              sx={{ justifyContent: 'flex-end' }}
                              spacing={1}
                            >
                              {token.manage_url && (
                                <Button
                                  color="primary"
                                  href={token.manage_url}
                                  target="_blank"
                                  variant="text"
                                >
                                  <FontAwesomeIcon
                                    icon={faArrowUpRightFromSquare}
                                    size="lg"
                                  />
                                </Button>
                              )}
                              <Button
                                color="primary"
                                onClick={() => handleOpenDialog(token)}
                              >
                                <FontAwesomeIcon icon={faEdit} size="lg" />
                              </Button>
                              <Button
                                color="primary"
                                onClick={() => handleDelete(token.id)}
                              >
                                <FontAwesomeIcon icon={faTrashCan} size="lg" />
                              </Button>
                            </Stack>
                          </TableCell>
                        </CheckPermissions>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </LargeCard>

      {dialogOpen && editingToken && (
        <AdminCanaryTokensDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          editingToken={editingToken}
          onSubmit={handleFormSubmit}
        />
      )}
      <DeleteModal />
    </Box>
  );
};

export default AdminCanaryTokens;
